import { IconSparkles } from "@tabler/icons-react"
import { type FC, useEffect, useMemo, useState } from "react"

import {
	aiGenerationCreditCosts,
	type LandingPageAudienceOfferType,
	type LandingPageCopyTone,
	type LandingPageGoal,
} from "@forento/core/models/ai"
import { type LandingPageStyleId, landingPageStyles } from "@forento/core/models/landingStyle"
import { type PlatformLanguageId, platformLanguageIds, platformLanguages } from "@forento/core/models/platform"
import { landingPageCopyCharacterLimits } from "@forento/core/schemas/landing"
import { toNumberFormat } from "@forento/core/utilities/number"

import AiGenerationDiscountNotice from "~/components/AiGenerationDiscountNotice"
import AiImagePicker from "~/components/AiImagePicker"
import Button, { SubmitButton } from "~/components/Button"
import Dropdown from "~/components/Dropdown"
import InputField, { InputTextArea } from "~/components/InputField"
import { DefaultModal, ModalButtons, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import useFileUpload from "~/hooks/useFileUpload"
import { captureForentoAiEvent, getBuyAiCreditsUrl, useForentoAiModalTracking } from "~/utilities/forentoAiAnalytics"
import { query } from "~/utilities/trpc"

import WebsiteStylePicker from "./WebsiteStylePicker"

const goalItems: { id: LandingPageGoal; title: string }[] = [
	{ id: "generate-leads", title: "Generate leads" },
	{ id: "drive-signups", title: "Drive sign-ups" },
	{ id: "drive-sales", title: "Drive sales" },
]

const audienceItems: { id: LandingPageAudienceOfferType; title: string }[] = [
	{ id: "beginner", title: "Beginner" },
	{ id: "professional", title: "Professional" },
	{ id: "student", title: "Student" },
	{ id: "premium", title: "Premium" },
	{ id: "high-ticket", title: "Ultra high-ticket" },
	{ id: "low-ticket", title: "Low-ticket" },
]

const toneItems: { id: LandingPageCopyTone; title: string }[] = [
	{ id: "friendly", title: "Friendly" },
	{ id: "salesy", title: "Salesy" },
	{ id: "authority-building", title: "Authority-building" },
]

const languageItems = platformLanguageIds.map(id => ({ id, title: platformLanguages[id].label }))

const LOADING_MESSAGES = [
	"Understanding the goal of the website",
	"Optimizing for your audience & offer type",
	"Establishing your copy tone",
	"Adding relevant blocks of content",
	"Designing your layout",
	"Learning from 6,000+ Forento sites",
	"Drafting headlines",
	"Polishing the design",
	"Understanding your brand voice",
	"Displaying your academy offerings",
]

const ROTATION_INTERVAL_MS = 2_800
const FADE_DURATION_MS = 300
const AI_INPUT_LIMITS = {
	suggestedTitle: landingPageCopyCharacterLimits.title,
	aboutYou: 1_000,
	aboutAcademy: 1_000,
	offer: landingPageCopyCharacterLimits.description,
	supportingImages: 8,
} as const

const shuffle = <T,>(items: readonly T[]): T[] => {
	const result = [...items]
	for (let i = result.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1))
		;[result[i], result[j]] = [result[j]!, result[i]!]
	}
	return result
}

type Props = {
	isOpen: boolean
	defaultLanguage: PlatformLanguageId
	onClose(): void
	onGenerated(generationId: number): Promise<void>
}
const AiLandingPageModal: FC<Props> = ({ isOpen, defaultLanguage, onClose, onGenerated }) => {
	const alert = useAlert()
	const user = useUser()
	const platform = usePlatform()!
	const inputLimits = user.user?.isImpersonating ? undefined : AI_INPUT_LIMITS

	const [suggestedTitle, setSuggestedTitle] = useState(CONFIG.isDevelopment ? "The Ultimate Academy" : "")
	const [aboutYou, setAboutYou] = useState(
		CONFIG.isDevelopment ? "I am an experienced online coach and educator with a passion for teaching." : "",
	)
	const [aboutAcademy, setAboutAcademy] = useState(
		CONFIG.isDevelopment
			? "My academy helps students reach their full potential through expert-led video courses and community support."
			: "",
	)
	const [offer, setOffer] = useState(
		CONFIG.isDevelopment
			? "Premium membership with exclusive course content, live Q&As, and downloadable resources."
			: "",
	)
	const [backgroundImage, setBackgroundImage] = useState<File | null>(null)
	const [supportingImages, setSupportingImages] = useState<File[]>([])
	const [goal, setGoal] = useState<LandingPageGoal>("drive-sales")
	const [audienceOfferType, setAudienceOfferType] = useState<LandingPageAudienceOfferType>("beginner")
	const [copyTone, setCopyTone] = useState<LandingPageCopyTone>("authority-building")
	const [language, setLanguage] = useState<PlatformLanguageId>(defaultLanguage)
	const [styleId, setStyleId] = useState<LandingPageStyleId>("clean")

	const imageUpload = useFileUpload()
	const generate = query.ai.generateLandingPage.useMutation({
		async onSuccess(result) {
			captureForentoAiEvent("forento_ai_generation_succeeded", { type: "landing-page" })
			await onGenerated(result.generationId)
			await user.reload()
			onClose()
			reset()
		},
		async onError(error) {
			console.error(error)
			captureForentoAiEvent("forento_ai_generation_failed", { type: "landing-page" })
			await alert.show("Error", error.message || "Failed to generate landing page. Please try again.")
		},
	})

	const generationCost = aiGenerationCreditCosts["landing-page"]
	const hasEnoughCredits = platform.aiCreditBalance >= generationCost
	useForentoAiModalTracking({
		type: "landing-page",
		isFormVisible: isOpen,
		creditBalance: platform.aiCreditBalance,
		cost: generationCost,
	})
	const isGenerating = imageUpload.isPending || generate.isPending
	const canGenerate =
		suggestedTitle.trim().length > 0 && backgroundImage !== null && hasEnoughCredits && !isGenerating

	const shuffledMessages = useMemo(() => shuffle(LOADING_MESSAGES), [])
	const [messageIndex, setMessageIndex] = useState(0)
	const [isMessageVisible, setIsMessageVisible] = useState(true)
	const [progress, setProgress] = useState(0)

	useEffect(() => {
		if (!isGenerating) {
			setMessageIndex(0)
			setIsMessageVisible(true)
			setProgress(0)
			return
		}

		setProgress(0)

		const progressInterval = setInterval(() => {
			setProgress(prev =>
				prev >= 93 ? prev : prev >= 85 ? prev + 0.05 : prev >= 70 ? prev + 0.1 : Math.min(85, prev + 0.2),
			)
		}, 50)

		const rotate = setInterval(() => {
			setIsMessageVisible(false)
			const fade = setTimeout(() => {
				setMessageIndex(prev => (prev + 1) % shuffledMessages.length)
				setIsMessageVisible(true)
			}, FADE_DURATION_MS)
			return () => clearTimeout(fade)
		}, ROTATION_INTERVAL_MS)

		return () => {
			clearInterval(progressInterval)
			clearInterval(rotate)
		}
	}, [isGenerating, shuffledMessages])

	const handleGenerate = async () => {
		if (!canGenerate || backgroundImage === null) return
		captureForentoAiEvent("forento_ai_generation_submitted", { type: "landing-page" })

		const supportingImageUrls: string[] = []
		let backgroundImageUrl: string
		try {
			const uploaded = await imageUpload.upload({ file: backgroundImage, kind: "landing-image" })
			if (uploaded === null) return
			backgroundImageUrl = uploaded.publicUrl
			for (const file of supportingImages) {
				const uploadedSupportingImage = await imageUpload.upload({ file, kind: "landing-image" })
				if (uploadedSupportingImage === null) return
				supportingImageUrls.push(uploadedSupportingImage.publicUrl)
			}
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to upload the images. Please try again.")
			return
		}

		generate.mutate({
			suggestedTitle,
			aboutYou,
			aboutAcademy,
			offer,
			backgroundImageUrl,
			supportingImageUrls,
			goal,
			audienceOfferType,
			copyTone,
			language,
			styleId,
		})
	}

	const reset = () => {
		setSuggestedTitle("")
		setAboutYou("")
		setAboutAcademy("")
		setOffer("")
		setBackgroundImage(null)
		setSupportingImages([])
		setGoal("drive-sales")
		setAudienceOfferType("beginner")
		setCopyTone("authority-building")
		setLanguage(defaultLanguage)
		setStyleId("clean")
	}

	if (isGenerating) {
		const displayProgress = Math.round(progress)
		return (
			<DefaultModal width={900} isOpen={isOpen} onSubmit={handleGenerate}>
				<div className="flex flex-col items-center gap-7 py-8 text-center">
					<div className="relative">
						<div className="bg-primary/20 absolute inset-0 animate-ping rounded-full" />
						<div className="bg-primary/10 absolute inset-0 animate-pulse rounded-full" />
						<div className="from-primary/15 to-primary/5 ring-primary/5 relative flex size-20 items-center justify-center rounded-full bg-linear-to-br ring-8">
							<IconSparkles className="text-primary size-10" strokeWidth={1.75} />
						</div>
					</div>

					<div className="flex max-w-lg flex-col gap-2">
						<h3 className="text-2xl font-semibold text-gray-900">Generating your site</h3>
						<span
							className="min-h-6 text-base text-gray-600 transition-opacity"
							style={{ opacity: isMessageVisible ? 1 : 0, transitionDuration: `${FADE_DURATION_MS}ms` }}
						>
							{shuffledMessages[messageIndex]}
						</span>
					</div>

					<div className="flex w-full max-w-md flex-col gap-2">
						<div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
							<div
								className="from-primary/70 to-primary h-full rounded-full bg-linear-to-r transition duration-700 ease-out"
								style={{ width: `${progress}%` }}
							/>
						</div>
						<div className="w-full text-center text-xs font-medium text-gray-500 tabular-nums">
							{displayProgress}%
						</div>
					</div>
				</div>
			</DefaultModal>
		)
	}

	return (
		<DefaultModal
			width={900}
			isOpen={isOpen}
			onSubmit={handleGenerate}
			onClose={() => {
				reset()
				onClose()
			}}
		>
			<ModalTitle>Generate landing page with Forento AI</ModalTitle>
			<ModalText>
				Answer a few setup questions and Forento AI will generate a ready-to-edit landing page based on your
				academy, products, media, and goals.
			</ModalText>
			<ModalInputs>
				<div className="grid gap-3 rounded-lg border border-gray-200 bg-white p-3 sm:grid-cols-3">
					<div>
						<div className="text-xs text-gray-500">Available balance</div>
						<div className="text-lg font-semibold text-gray-900">
							{toNumberFormat(platform.aiCreditBalance)} credits
						</div>
					</div>
					<div>
						<div className="text-xs text-gray-500">This generation costs</div>
						<div className="text-lg font-semibold text-gray-900">
							{toNumberFormat(generationCost)} credits
						</div>
					</div>
					<div className="flex items-center sm:justify-end">
						<Button
							variant="secondary"
							onClick={getBuyAiCreditsUrl("landing-page")}
							className="w-full sm:w-auto"
						>
							Purchase credits
						</Button>
					</div>
					<AiGenerationDiscountNotice className="sm:col-span-3" />
					{!hasEnoughCredits && (
						<p className="text-sm text-red-600 sm:col-span-3">
							Purchase more credits before generating a landing page.
						</p>
					)}
				</div>
				<div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
					<p className="text-sm text-amber-900">
						Forento AI will create your website pages first. You can review and edit everything after
						generation.
					</p>
				</div>

				<div className="grid gap-4 lg:grid-cols-2">
					<InputField
						label="Suggested title"
						value={suggestedTitle}
						onChange={setSuggestedTitle}
						placeholder="The Complete Guitar Academy"
						maxCharacters={inputLimits?.suggestedTitle}
						required
					/>
					<Dropdown
						label="Language"
						items={languageItems}
						selectedItemId={language}
						onChange={id => setLanguage(id as PlatformLanguageId)}
					/>
				</div>

				<div className="grid gap-4 lg:grid-cols-2">
					<InputTextArea
						label="About yourself"
						value={aboutYou}
						onChange={setAboutYou}
						placeholder="A few sentences about your background, expertise, and teaching style"
						maxCharacters={inputLimits?.aboutYou}
					/>
					<InputTextArea
						label="About the academy"
						value={aboutAcademy}
						onChange={setAboutAcademy}
						placeholder="A few sentences about what learners get from your academy"
						maxCharacters={inputLimits?.aboutAcademy}
					/>
				</div>

				<InputTextArea
					label="Offer"
					value={offer}
					onChange={setOffer}
					placeholder="Any promotion, deal, product, or membership angle you want to highlight"
					maxCharacters={inputLimits?.offer}
				/>

				<div className="grid gap-4 lg:grid-cols-2">
					<AiImagePicker
						label="Background image"
						description="Required for the hero section"
						files={backgroundImage ? [backgroundImage] : []}
						onChange={files => setBackgroundImage(files[0] ?? null)}
					/>
					<AiImagePicker
						label="Supporting images"
						description="Optional images for gallery and content sections"
						files={supportingImages}
						onChange={setSupportingImages}
						maxFiles={inputLimits?.supportingImages}
						multiple
					/>
				</div>

				<div className="grid gap-4 lg:grid-cols-3">
					<Dropdown
						label="Goal of the website"
						items={goalItems}
						selectedItemId={goal}
						onChange={id => setGoal(id as LandingPageGoal)}
					/>
					<Dropdown
						label="Audience & offer type"
						items={audienceItems}
						selectedItemId={audienceOfferType}
						onChange={id => setAudienceOfferType(id as LandingPageAudienceOfferType)}
					/>
					<Dropdown
						label="Copy tone"
						items={toneItems}
						selectedItemId={copyTone}
						onChange={id => setCopyTone(id as LandingPageCopyTone)}
					/>
				</div>

				<div className="flex flex-col gap-2">
					<span className="text-sm font-semibold text-gray-700">Website style</span>
					<WebsiteStylePicker
						value={styleId}
						brandColor={platform.primaryColor}
						usesBrandColor
						onChange={setStyleId}
						className="sm:grid-cols-4"
					/>
					<span className="text-xs text-gray-500">
						{landingPageStyles[styleId].description} You can switch styles any time in the editor.
					</span>
				</div>
			</ModalInputs>
			<ModalButtons>
				<Button
					variant="secondary"
					onClick={() => {
						reset()
						onClose()
					}}
				>
					Cancel
				</Button>
				<SubmitButton variant="primary" isDisabled={!canGenerate}>
					<IconSparkles className="size-4" />
					Generate landing page ({toNumberFormat(generationCost)} credits)
				</SubmitButton>
			</ModalButtons>
		</DefaultModal>
	)
}

export default AiLandingPageModal
